// IE Hub - Radio Group Component
// Enhanced radio button group with title and description support

import React, { forwardRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup as MuiRadioGroup,
  Typography,
  alpha,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon
} from '@mui/icons-material';
import ConditionalContainer from '../layout/ConditionalContainer';

interface ValidationRules {
  required?: boolean;
  requiredMessage?: string;
}

interface RadioOption {
  value: string | number;
  title: string;
  description?: string;
  disabled?: boolean;
}

type RadioOptionInput = RadioOption | string | number | Record<string, unknown>;

interface RadioGroupProps {
  label?: string;
  value?: string | number;
  onChange?: (value: string | number, event?: React.ChangeEvent<HTMLInputElement>) => void;
  options: Array<RadioOptionInput>;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  row?: boolean;
  variant?: 'standard' | 'outlined' | 'soft';
  sx?: Record<string, unknown>;
  name?: string;
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: 'paper' | 'outlined' | 'plain';
  showErrorInContainer?: boolean;
  [key: string]: any;
}

// Styled components
const StyledFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

const StyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  padding: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-checked': {
    '& .MuiSvgIcon-root': {
      animation: 'radioCheck 0.2s ease-in-out',
      color: isSelected ? theme.palette.common.white : theme.palette.primary.main,
    },
  },
  '& .MuiSvgIcon-root': {
    color: isSelected ? theme.palette.common.white : 'inherit',
  },
  '@keyframes radioCheck': {
    '0%': {
      transform: 'scale(0.8)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

const OptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'selected' && prop !== 'disabled' && prop !== 'hasDescription',
})<{ variant?: string; selected?: boolean; disabled?: boolean; hasDescription?: boolean }>(
  ({ theme, variant, selected, disabled, hasDescription }) => ({
    display: 'flex',
    alignItems: hasDescription ? 'flex-start' : 'center',
    padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 1.5,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',

    // Hover border color changes only
    '&:hover:not([data-disabled="true"])': {
      borderColor: `${theme.palette.primary.main} !important`,
    },

    ...(variant === 'outlined' && {
      border: `1px solid ${selected
        ? theme.palette.primary.main
        : disabled
          ? theme.palette.action.disabled
          : theme.palette.divider
        }`,
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.92)
        : theme.palette.background.paper,
      '&:hover': !disabled && {
        borderColor: theme.palette.primary.main,
      },
    }),

    ...(variant === 'soft' && {
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.92)
        : disabled
          ? alpha(theme.palette.action.disabled, 0.05)
          : alpha(theme.palette.background.default, 0.4),
      border: `1px solid ${selected
        ? alpha(theme.palette.primary.main, 0.4)
        : 'transparent'
        }`,
      '&:hover': !disabled && {
        border: `1px solid ${theme.palette.primary.main}`,
      },
    }),

    ...(variant === 'standard' && {
      backgroundColor: 'transparent',
      '&:hover': !disabled && {
        borderColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
      },
    }),

    ...(disabled && {
      opacity: 0.6,
      '&:hover': {
        borderColor: theme.palette.action.disabled,
      },
    }),
  })
);

const ContentContainer = styled(Box)(() => ({
  flex: 1,
  marginLeft: 8,
  display: 'flex',
  flexDirection: 'column',
}));

const TitleText = styled(Typography)<{ selected?: boolean; disabled?: boolean }>(
  ({ theme, selected, disabled }) => ({
    fontWeight: selected ? 600 : 500,
    color: disabled
      ? theme.palette.text.disabled
      : selected
        ? theme.palette.common.white
        : theme.palette.text.primary,
    lineHeight: 1.4,
    marginBottom: 0,
    transition: 'color 0.2s ease-in-out',
  })
);

const DescriptionText = styled(Typography)<{ disabled?: boolean; selected?: boolean }>(
  ({ theme, disabled, selected }) => ({
    color: disabled
      ? theme.palette.text.disabled
      : selected
        ? alpha(theme.palette.common.white, 0.8)
        : theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
    transition: 'color 0.2s ease-in-out',
  })
);

const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(({
  label,
  value = '',
  onChange,
  options = [],
  validation = {},
  onValidate,
  error = false,
  helperText,
  disabled = false,
  size = 'medium',
  color = 'primary',
  row = false,
  variant = 'outlined',
  sx = {},
  name,
  // ConditionalContainer props
  title,
  description,
  containerVariant = 'plain',
  showErrorInContainer = true
}, _ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [validationError, setValidationError] = useState('');

  // Normalize options
  const normalizedOptions = React.useMemo<RadioOption[]>(() => {
    return (options || []).map((opt: RadioOptionInput) => {
      if (typeof opt === 'string' || typeof opt === 'number') {
        return { value: opt, title: String(opt) };
      }
      const obj = opt as Record<string, unknown>;
      const value = (obj.value as string | number | undefined)
        ?? (obj.id as string | number | undefined)
        ?? (obj.key as string | number | undefined)
        ?? (obj.code as string | number | undefined)
        ?? (obj.title as string | undefined)
        ?? (obj.label as string | undefined)
        ?? '';
      const title = (obj.title as string | undefined)
        ?? (obj.label as string | undefined)
        ?? String(value);
      const description = (obj.description as string | undefined)
        ?? (obj.helperText as string | undefined)
        ?? (obj.subtitle as string | undefined)
        ?? (obj.subTitle as string | undefined);
      const disabled = Boolean((obj.disabled as boolean | undefined) ?? (obj.isDisabled as boolean | undefined));
      return {
        value,
        title,
        description,
        disabled
      };
    });
  }, [options]);

  // Validation logic
  const validateInput = (inputValue: string | number) => {
    let errorMsg = '';

    if (validation.required && (!inputValue || inputValue === '')) {
      errorMsg = validation.requiredMessage || 'Please select an option';
    }

    setValidationError(errorMsg);

    if (onValidate) {
      onValidate(errorMsg === '', errorMsg);
    }

    return errorMsg === '';
  };

  // Handle radio change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Validate
    if (Object.keys(validation).length > 0) {
      validateInput(newValue);
    }

    if (onChange) {
      onChange(newValue, event);
    }
  };

  // Handle option container click
  const handleOptionClick = (optionValue: string | number, optionDisabled?: boolean) => {
    if (disabled || optionDisabled) return;

    // Create synthetic event
    const syntheticEvent = {
      target: { value: String(optionValue) }
    } as React.ChangeEvent<HTMLInputElement>;

    handleChange(syntheticEvent);
  };

  const hasError = !!validationError || error;
  const displayHelperText: React.ReactNode = validationError || helperText;

  // Custom icon props for radio
  const radioIconProps = {
    icon: <RadioButtonUncheckedIcon />,
    checkedIcon: <RadioButtonCheckedIcon />,
  };

  // Determine if we should show error in container
  const shouldShowErrorInContainer = showErrorInContainer && hasError && validationError;

  // Content to render inside ConditionalContainer
  const radioContent = (
    <StyledFormControl error={hasError && !shouldShowErrorInContainer} sx={{ width: '100%' }}>
      {label && !title && (
        <Typography
          variant="subtitle1"
          component="legend"
          sx={{
            mb: 0.75,
            color: 'primary.main',
            fontSize: isMobile ? '14px' : '16px',
            lineHeight: isMobile ? '20px' : '22px',
          }}
        >
          {label}
          {validation.required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          )}
        </Typography>
      )}

      {/* Error message above radio options - same as TextField */}
      {validationError && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mb: 1,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: isMobile ? '16px' : '20px',
            fontWeight: 500
          }}
        >
          {validationError}
        </Typography>
      )}

      <MuiRadioGroup
        value={value}
        onChange={handleChange}
        name={name}
        row={row}
        sx={{
          gap: variant === 'standard' ? 0.5 : (isMobile ? 1.5 : 2.25),
        }}
      >
        {normalizedOptions.map((option: RadioOption) => {
          const isSelected = value === option.value;
          const isDisabled = option.disabled || disabled;

          return (
            <OptionContainer
              key={option.value}
              variant={variant}
              selected={isSelected}
              disabled={isDisabled}
              hasDescription={!!option.description}
              onClick={() => handleOptionClick(option.value, option.disabled)}
              data-disabled={isDisabled}
            >
              <FormControlLabel
                value={option.value}
                control={
                  <StyledRadio
                    size={size}
                    color={color}
                    disabled={isDisabled}
                    isSelected={isSelected}
                    {...radioIconProps}
                    sx={{
                      padding: variant === 'standard' ? 1 : 0.75,
                      alignSelf: option.description ? 'flex-start' : 'center',
                    }}
                  />
                }
                label=""
                sx={{
                  margin: 0,
                  alignItems: option.description ? 'flex-start' : 'center',
                }}
              />

              <ContentContainer>
                <TitleText
                  variant="body1"
                  selected={isSelected}
                  disabled={isDisabled}
                  className="option-title"
                >
                  {option.title}
                </TitleText>

                {option.description && (
                  <DescriptionText
                    variant="body2"
                    disabled={isDisabled}
                    selected={isSelected}
                    className="option-description"
                    dangerouslySetInnerHTML={{ __html: option.description }}
                  />
                )}
              </ContentContainer>
            </OptionContainer>
          );
        })}
      </MuiRadioGroup>

      {displayHelperText && !shouldShowErrorInContainer && (
        <FormHelperText error={hasError} sx={{ mt: 1.5, ml: 0 }}>
          {displayHelperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );

  const containerProps: any = {
    ifError: false, // Never show error in container - handle errors in RadioGroup like TextField
    variant: containerVariant,
    sx: {
      ...(sx as Record<string, unknown>),
      // Add red left border when there's a validation error - same as TextField
      ...(validationError && {
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        paddingLeft: 2,
        paddingBottom: 0, // Reduce bottom padding to minimize border height
        minHeight: 'auto',
        height: 'fit-content',
        maxHeight: '90%', // or whatever height you prefer
      }),
      // Ensure consistent width regardless of error state
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch', // Changed from flex-start to stretch to maintain full width
      gap: isMobile ? 1.5 : 2.25,
      mb: isMobile ? 2.25 : 2.5,
      // Reduce overall container height when in error state
      ...(validationError && {
        paddingTop: 0,
        marginBottom: 0,
      })
    },
  };

  if (title || (label && title !== undefined)) {
    containerProps.title = title || label;
  }

  if (description) {
    containerProps.description = description;
  }

  // Remove container error message logic since we handle errors in RadioGroup like TextField
  // if (containerErrorMessage) {
  //   containerProps.errorMessage = containerErrorMessage;
  // }

  return (
    <ConditionalContainer {...containerProps}>
      {radioContent}
    </ConditionalContainer>
  );
});

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
